import { BaseAPIClient } from "@/plugins/baseApi";

const POST = "POST";
const GET = "GET";
const PUT = "PUT";
const DELETE = "DELETE";

// Filtro vazio nao vira query string. Sem isso o axios manda `?status=` e o
// backend precisa distinguir "vazio" de "nao enviado" em cada parametro.
const activeFilters = (filters = {}) =>
  Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) => value !== null && value !== undefined && value !== ""
    )
  );
export class PitchApi extends BaseAPIClient {
  signIn(credentials) {
    return this.call("/sign-in", POST, credentials);
  }

  // Dashboard/cockpit: KPIs + curvas mensais. months = janela do filtro.
  getDashboard(months = 3) {
    return this.call("/dashboard", GET, { months });
  }

  // Admin logado: usuario, perfil e permissoes.
  getMe() {
    return this.call("/me", GET);
  }

  // Perfis e permissoes (so o Administrador).
  getPermissionCatalog() {
    return this.call("/permissions/catalog", GET);
  }

  listProfiles() {
    return this.call("/profiles", GET);
  }

  getProfile(id) {
    return this.call(`/profiles/${id}`, GET);
  }

  createProfile(body) {
    return this.call("/profiles", POST, body);
  }

  updateProfile(id, body) {
    return this.call(`/profiles/${id}`, PUT, body);
  }

  deleteProfile(id, password) {
    return this.call(`/profiles/${id}`, DELETE, { password });
  }

  // users routes
  createUser(body) {
    return this.call("/users", POST, body);
  }

  updateUser(id, body) {
    return this.call(`/users/${id}`, PUT, body);
  }

  listUsers(filters) {
    return this.call("/users", GET, activeFilters(filters));
  }

  // customers routes
  listCustomers(filters) {
    return this.call("/customers", GET, activeFilters(filters));
  }

  listOneCustomer(id) {
    return this.call(`/customers/${id}`, GET);
  }

  // Retorno de 22/10 (card 17trt6jcyfb): exercícios de treinamento do
  // professor, corrigidos pelo administrativo.
  getPartnerTrainingExercises(partnerId) {
    return this.call(`/partners/${partnerId}/training-exercises`, GET);
  }

  getTrainingAttempt(attemptId) {
    return this.call(`/training-attempts/${attemptId}`, GET);
  }

  setTrainingCorrection(attemptId, itemId, correct) {
    return this.call(
      `/training-attempts/${attemptId}/items/${itemId}/correction`,
      PUT,
      { correct }
    );
  }

  setTrainingReviewed(attemptId, reviewed) {
    return this.call(`/training-attempts/${attemptId}/reviewed`, PUT, {
      reviewed,
    });
  }

  saveTrainingFeedback(attemptId, text) {
    return this.call(`/training-attempts/${attemptId}/feedback`, PUT, {
      text,
    });
  }

  listCustomerMonthlyCharges(id) {
    return this.call(`/customers/${id}/monthly-charges`, GET);
  }

  // Card 17trt6je251: valor e/ou status da mensalidade do aluno Autonomy.
  updateSoloCharge(customerId, chargeId, body) {
    return this.call(
      `/customers/${customerId}/monthly-charges/${chargeId}`,
      "PATCH",
      body
    );
  }

  listCharges(filters) {
    return this.call("/charges", GET, activeFilters(filters));
  }

  createCustomer(body) {
    return this.call("/customers", POST, body);
  }

  updateCustomer(id, body) {
    return this.call(`/customers/${id}`, PUT, body);
  }

  // Alunos que optaram por estudar sem professor. O recorte roda no servidor
  // porque a listagem e paginada.
  listCustomersWithoutTeacher(filters) {
    return this.call("/customers", GET, {
      ...activeFilters(filters),
      withoutTeacher: true,
    });
  }

  updateCustomerLevel(id, level) {
    return this.call(`/customers/${id}/level`, PUT, { level });
  }

  getSoloOverview() {
    return this.call("/customers/solo-overview", GET);
  }

  deactivateMany(body) {
    return this.call("/customers/deactivate/status", PUT, body);
  }

  resetPasswords(body) {
    return this.call("/customers/password/reset", PUT, body);
  }

  resendActivationEmail(id) {
    return this.call(`/customers/${id}/resend-activation`, POST);
  }

  // partners routes

  listPartners(filters) {
    return this.call("/partners", GET, activeFilters(filters));
  }

  listOnePartner(id) {
    return this.call(`/partners/${id}`, GET);
  }

  // Tutores (fluxo do aluno solo): usado para designar um tutor a um aluno.
  listTutors() {
    return this.call("/partners/tutors", GET);
  }

  // Lista enxuta (id + nome) para o filtro "Professor" da tela de alunos.
  listPartnerOptions() {
    return this.call("/partners/options", GET);
  }

  createPartner(body) {
    return this.call("/partners", POST, body);
  }

  updatePartner(id, body) {
    return this.call(`/partners/${id}`, PUT, body);
  }

  resetPartnerPasswords(body) {
    return this.call("/partners/password/reset", PUT, body);
  }

  deactivateManyPartners(body) {
    return this.call("/partners/deactivate/status", PUT, body);
  }

  // study groups routes
  listStudyGroups(filters) {
    return this.call("/groups", GET, activeFilters(filters));
  }

  createStudyGroup(body) {
    return this.call("/groups", POST, body);
  }

  // Relatório de acompanhamento de turmas e alunos (card 86akputj7).
  getGroupsReport(filters) {
    return this.call("/reports/groups", GET, activeFilters(filters));
  }

  // Card 86akputj7: Dashboard Geral de Relatorios (tela inicial). Uma chamada
  // traz todos os blocos e listagens no recorte dos filtros globais.
  getManagementReport(filters) {
    return this.call("/reports/management", GET, activeFilters(filters));
  }

  updateStudyGroup(id, body) {
    return this.call(`/groups/${id}`, PUT, body);
  }

  updateStudyGroupStudents(id, body) {
    return this.call(`/groups/${id}/students`, PUT, body);
  }

  listStudyGroupEvents(id) {
    return this.call(`/groups/${id}/events`, GET);
  }

  syncStudyGroupEvents(id) {
    return this.call(`/groups/${id}/events/sync`, POST);
  }

  deleteStudyGroup(id) {
    return this.call(`/groups/${id}`, DELETE);
  }

  // Exclusão de conteúdo (soft delete). A senha vai no corpo, não na URL:
  // query string acaba em log de acesso e no histórico do navegador.
  deleteCourse(id, password) {
    return this.call(`/course/${id}`, DELETE, { password });
  }

  deleteCourseClass(id, password) {
    return this.call(`/course/classes/${id}`, DELETE, { password });
  }

  deleteClassContent(id, password) {
    return this.call(`/course/content/${id}`, DELETE, { password });
  }

  // monitoramento / auditoria

  updateGeneralClassLink(id, link) {
    return this.call(`/general-classes/${id}/link`, PUT, { link });
  }

  // Aplica nas aulas já agendadas a configuração de acesso do Meet que as
  // novas já nascem tendo (card 86ak74554).
  backfillMeetAccess() {
    return this.call("/meet-access/backfill", PUT);
  }

  listNoteShares(filters) {
    return this.call("/monitoring/note-shares", GET, activeFilters(filters));
  }

  // O anexo é endereçado pelo compartilhamento: é ele que prova que o arquivo
  // foi entregue àquele aluno, naquela data.
  getNoteShareAttachment(shareId, fileId) {
    return this.call(
      `/monitoring/note-shares/${shareId}/attachments/${fileId}`,
      GET
    );
  }

  // general classes routes

  listGeneralClasses(filters) {
    return this.call("/general-classes", GET, activeFilters(filters));
  }

  getGeneralClass(id) {
    return this.call(`/general-classes/${id}`, GET);
  }

  createGeneralClass(body) {
    return this.call("/general-classes", POST, body);
  }

  updateGeneralClass(id, body) {
    return this.call(`/general-classes/${id}`, PUT, body);
  }

  deleteGeneralClass(id) {
    return this.call(`/general-classes/${id}`, DELETE);
  }

  // Card 17trt6jcypk: chamada da aula geral pelo administrativo.
  getGeneralClassAttendance(id) {
    return this.call(`/general-classes/${id}/attendance`, GET);
  }

  saveGeneralClassAttendance(id, attendances) {
    return this.call(`/general-classes/${id}/attendance`, PUT, {
      attendances,
    });
  }

  searchGeneralClassStudents(id, search) {
    return this.call(`/general-classes/${id}/eligible-students`, GET, {
      search,
    });
  }

  // Curso

  getAllModules(query) {
    return this.call("/course", GET, activeFilters(query));
  }

  getModule(id) {
    return this.call(`/course/${id}`, GET);
  }

  getModuleContentByLevel(data) {
    return this.call("/course/classes/by-level", GET, data);
  }

  getModuleClasses(moduleId, classId) {
    return this.call(`/course/${moduleId}/classes/${classId}`, GET);
  }

  createModule(data) {
    return this.call("/course", POST, data);
  }

  updateModule(id, data) {
    return this.call(`/course/${id}`, PUT, data);
  }

  createModuleClass(moduleId, data) {
    return this.call(`/course/${moduleId}/classes`, POST, data);
  }

  updateModuleClass(moduleId, data) {
    return this.call(`/course/${moduleId}/classes`, PUT, data);
  }

  getUploadUrl(fileContent) {
    return this.call("/upload/url", POST, fileContent);
  }

  deleteFile(id) {
    return this.call(`/delete-files/${id}`, DELETE);
  }

  updateOrder(moduleId, data) {
    return this.call(`/course/${moduleId}/classes/order`, PUT, data);
  }

  // Sequência dos materiais dentro de uma aula. `data` é [{ id, order }], onde
  // `id` é o pivotId — o mesmo arquivo pode estar em outras aulas, com ordem
  // própria em cada uma.
  updateContentOrder(classId, data) {
    return this.call(`/course/classes/${classId}/content/order`, PUT, data);
  }

  // Central de Comunicacao (card 86akj8ptp)
  estimateCommunicationAudience(audience) {
    return this.call("/communications/audience/estimate", POST, { audience });
  }

  createCommunication(body) {
    return this.call("/communications", POST, body);
  }

  listCommunications(filters) {
    return this.call("/communications", GET, activeFilters(filters));
  }

  getCommunication(id) {
    return this.call(`/communications/${id}`, GET);
  }

  getCommunicationsOverview() {
    return this.call("/communications/overview", GET);
  }

  // Rascunho ou programada: o corpo é o mesmo da criação.
  updateCommunication(id, body) {
    return this.call(`/communications/${id}`, PUT, body);
  }

  cancelCommunication(id) {
    return this.call(`/communications/${id}/cancel`, POST);
  }

  pinCommunication(id, pinned, expiresAt) {
    return this.call(`/communications/${id}/pin`, PUT, {
      pinned,
      ...(expiresAt !== undefined ? { expiresAt } : {}),
    });
  }

  // Busca de aluno para "Aluno específico" (retorno de 26/09): consulta
  // própria, sem acento e por palavra. Responde { data: [{ id, name, email,
  // status, statusLabel, active }] }.
  searchCommunicationCustomers(q) {
    return this.call("/communications/recipients/customers", GET, { q });
  }

  // Só modelos podem ser excluídos.
  deleteCommunicationTemplate(id) {
    return this.call(`/communications/${id}`, DELETE);
  }

  // ===== Mensagens / chat (card 17trt6jbffe) =====
  // Ids e cursores sao texto (INT8 passa de MAX_SAFE_INTEGER): nunca Number().

  // filter: all | customers | partners | groups | support; status: open | closed
  listMessageConversations(filters) {
    return this.call("/messages/conversations", GET, activeFilters(filters));
  }

  getMessageConversation(id) {
    return this.call(`/messages/conversations/${id}`, GET);
  }

  // Sem cursor = abertura (grava log de acesso). `after` = polling.
  listConversationMessages(id, params = {}) {
    return this.call(
      `/messages/conversations/${id}/messages`,
      GET,
      activeFilters(params)
    );
  }

  // So em conversa de suporte (nas de aula a API responde 422).
  sendSupportMessage(id, { body, file } = {}) {
    const form = new FormData();
    if (body) form.append("body", body);
    if (file) form.append("file", file);
    return this.call(`/messages/conversations/${id}/messages`, POST, form);
  }

  markSupportConversationRead(id, upTo) {
    return this.call(
      `/messages/conversations/${id}/read`,
      POST,
      upTo ? { upTo: String(upTo) } : {}
    );
  }

  closeSupportConversation(id) {
    return this.call(`/messages/conversations/${id}/close`, POST);
  }

  reopenSupportConversation(id) {
    return this.call(`/messages/conversations/${id}/reopen`, POST);
  }

  listConversationAccessLogs(id) {
    return this.call(`/messages/conversations/${id}/access-logs`, GET);
  }

  // { customerId | partnerId, body }
  startSupportConversation(body) {
    return this.call("/messages/support/start", POST, body);
  }

  searchMessageRecipients(q) {
    return this.call("/messages/recipients", GET, { q });
  }

  getSupportUnread() {
    return this.call("/messages/unread", GET);
  }

  getMessagesSettings() {
    return this.call("/messages/settings", GET);
  }

  saveMessagesSettings(body) {
    return this.call("/messages/settings", PUT, body);
  }

  // Formulários de pesquisa (card 86akqxu0h). O envio sai pela Central:
  // createCommunication com `surveyId`.
  listSurveys(filters) {
    return this.call("/surveys", GET, activeFilters(filters));
  }

  createSurvey(body) {
    return this.call("/surveys", POST, body);
  }

  getSurvey(id) {
    return this.call(`/surveys/${id}`, GET);
  }

  updateSurvey(id, body) {
    return this.call(`/surveys/${id}`, PUT, body);
  }

  deleteSurvey(id) {
    return this.call(`/surveys/${id}`, DELETE);
  }

  duplicateSurvey(id) {
    return this.call(`/surveys/${id}/duplicate`, POST);
  }

  closeSurvey(id) {
    return this.call(`/surveys/${id}/close`, POST);
  }

  reopenSurvey(id) {
    return this.call(`/surveys/${id}/reopen`, POST);
  }

  getSurveyRecipients(id, status) {
    return this.call(
      `/surveys/${id}/recipients`,
      GET,
      activeFilters({ status })
    );
  }

  getSurveyResponses(id) {
    return this.call(`/surveys/${id}/responses`, GET);
  }

  remindSurvey(id, body = {}) {
    return this.call(`/surveys/${id}/remind`, POST, body);
  }

  // Exercicios (card 17trt6jcyfb): catalogo importado e vinculo com as aulas.
  listExerciseSets(filters) {
    return this.call("/exercises/sets", GET, activeFilters(filters));
  }

  getExerciseSet(id) {
    return this.call(`/exercises/sets/${id}`, GET);
  }

  updateExerciseSetSection(id, section) {
    return this.call(`/exercises/sets/${id}/section`, PUT, { section });
  }

  // Editor (21/10): questionarios e exercicios criados no Connect.
  createExerciseSet(payload) {
    return this.call("/exercises/sets", POST, payload);
  }

  updateExerciseSet(id, payload) {
    return this.call(`/exercises/sets/${id}`, PUT, payload);
  }

  deleteExerciseSet(id) {
    return this.call(`/exercises/sets/${id}`, DELETE);
  }

  createExercise(setId, payload) {
    return this.call(`/exercises/sets/${setId}/exercises`, POST, payload);
  }

  reorderExercises(setId, exerciseIds) {
    return this.call(`/exercises/sets/${setId}/exercises/order`, PUT, {
      exerciseIds,
    });
  }

  updateExercise(id, payload) {
    return this.call(`/exercises/pages/${id}`, PUT, payload);
  }

  deleteExercise(id) {
    return this.call(`/exercises/pages/${id}`, DELETE);
  }

  getExerciseReview() {
    return this.call("/exercises/review", GET);
  }

  confirmExerciseLink(linkId) {
    return this.call(`/exercises/links/${linkId}/confirm`, PUT);
  }

  dismissExerciseLink(linkId) {
    return this.call(`/exercises/links/${linkId}`, DELETE);
  }

  getClassExerciseSets(classId) {
    return this.call(`/course/classes/${classId}/exercise-sets`, GET);
  }

  linkExerciseSet(classId, setId) {
    return this.call(`/course/classes/${classId}/exercise-sets`, POST, {
      setId,
    });
  }

  reorderClassExerciseSets(classId, linkIds) {
    return this.call(`/course/classes/${classId}/exercise-sets/order`, PUT, {
      linkIds,
    });
  }

  // Games da aula (retorno de 18/10, card 17trt6jcyfb).
  getClassGames(classId) {
    return this.call(`/course/classes/${classId}/games`, GET);
  }

  createClassGame(classId, { title, embed }) {
    return this.call(`/course/classes/${classId}/games`, POST, {
      title,
      embed,
    });
  }

  removeClassGame(gameId) {
    return this.call(`/course/games/${gameId}`, DELETE);
  }

  reorderClassGames(classId, gameIds) {
    return this.call(`/course/classes/${classId}/games/order`, PUT, {
      gameIds,
    });
  }

  // Pitch Coins (card 17trt6jd8uu): regras, campanhas, lancamentos, extratos,
  // alertas e importacao de saldos.
  listCoinRules(filters) {
    return this.call("/coins/rules", GET, activeFilters(filters));
  }

  getCoinRule(id) {
    return this.call(`/coins/rules/${id}`, GET);
  }

  createCoinRule(body) {
    return this.call("/coins/rules", POST, body);
  }

  updateCoinRule(id, body) {
    return this.call(`/coins/rules/${id}`, PUT, body);
  }

  // So regra manual; os coins ja lancados por ela ficam no extrato.
  deleteCoinRule(id) {
    return this.call(`/coins/rules/${id}`, DELETE);
  }

  addCoinRuleVersion(id, body) {
    return this.call(`/coins/rules/${id}/versions`, POST, body);
  }

  listCoinCampaigns() {
    return this.call("/coins/campaigns", GET);
  }

  createCoinCampaign(body) {
    return this.call("/coins/campaigns", POST, body);
  }

  updateCoinCampaign(id, body) {
    return this.call(`/coins/campaigns/${id}`, PUT, body);
  }

  searchCoinOwners(q) {
    return this.call("/coins/owners", GET, { q });
  }

  getCoinOwner(ownerType, ownerId) {
    return this.call(`/coins/owners/${ownerType}/${ownerId}`, GET);
  }

  listCoinMovements(filters) {
    return this.call("/coins/movements", GET, activeFilters(filters));
  }

  launchCoins(body) {
    return this.call("/coins/launches", POST, body);
  }

  adjustCoins(body) {
    return this.call("/coins/adjustments", POST, body);
  }

  reverseCoinMovement(id, reason) {
    return this.call(`/coins/movements/${id}/reverse`, POST, { reason });
  }

  listCoinAlerts(filters) {
    return this.call("/coins/alerts", GET, activeFilters(filters));
  }

  resolveCoinAlert(id, body) {
    return this.call(`/coins/alerts/${id}/resolve`, POST, body);
  }

  previewCoinImport(csv) {
    return this.call("/coins/imports/preview", POST, { csv });
  }

  confirmCoinImport(csv) {
    return this.call("/coins/imports", POST, { csv });
  }

  listCoinBenefits() {
    return this.call("/coins/benefits", GET);
  }

  createCoinBenefit(body) {
    return this.call("/coins/benefits", POST, body);
  }

  updateCoinBenefit(id, body) {
    return this.call(`/coins/benefits/${id}`, PUT, body);
  }

  listCoinRedemptions(filters) {
    return this.call("/coins/redemptions", GET, activeFilters(filters));
  }

  approveCoinRedemption(id, note) {
    return this.call(`/coins/redemptions/${id}/approve`, POST, { note });
  }

  rejectCoinRedemption(id, reason) {
    return this.call(`/coins/redemptions/${id}/reject`, POST, { reason });
  }

  getCoinOwnerBadges(ownerType, ownerId) {
    return this.call(`/coins/owners/${ownerType}/${ownerId}/badges`, GET);
  }

  getCoinBadgeSettings() {
    return this.call("/coins/badges/settings", GET);
  }

  saveCoinBadgeSetting(body) {
    return this.call("/coins/badges/settings", PUT, body);
  }

  grantCoinBadge(body) {
    return this.call("/coins/badges/grant", POST, body);
  }

  revokeCoinBadgeAward(id) {
    return this.call(`/coins/badges/awards/${id}/revoke`, POST);
  }

  // Certificados (card 17trt6jfkw8): emitidos pelos alunos e modelo para
  // visualizar. Os PDFs vêm como arquivo (blob).
  listCertificates(filters) {
    return this.call("/certificates", GET, activeFilters(filters));
  }

  getCertificatePdf(id) {
    return this.call(`/certificates/${id}/pdf`, GET, undefined, {
      responseType: "blob",
    });
  }

  renderCertificatePdf(body) {
    return this.call("/certificates/render", POST, body, {
      responseType: "blob",
    });
  }

  searchCertificateStudents(q) {
    return this.call("/certificates/students", GET, { q });
  }

  getCertificateSuggestion(customerId) {
    return this.call(`/certificates/students/${customerId}/suggestion`, GET);
  }

  issueCertificate(body) {
    return this.call("/certificates", POST, body);
  }

  // Planos de estudo (card 86akj8q08): modelos, envio a alunos autônomos e
  // acompanhamento.
  searchStudyPlanLessons(q) {
    return this.call("/study-plans/lessons", GET, activeFilters({ q }));
  }

  searchStudyPlanStudents(q) {
    return this.call("/study-plans/students", GET, activeFilters({ q }));
  }

  listStudyPlanTemplates(filters) {
    return this.call("/study-plans/templates", GET, activeFilters(filters));
  }

  getStudyPlanTemplate(id) {
    return this.call(`/study-plans/templates/${id}`, GET);
  }

  createStudyPlanTemplate(body) {
    return this.call("/study-plans/templates", POST, body);
  }

  updateStudyPlanTemplate(id, body) {
    return this.call(`/study-plans/templates/${id}`, PUT, body);
  }

  duplicateStudyPlanTemplate(id) {
    return this.call(`/study-plans/templates/${id}/duplicate`, POST);
  }

  deleteStudyPlanTemplate(id) {
    return this.call(`/study-plans/templates/${id}`, DELETE);
  }

  sendStudyPlan(body) {
    return this.call("/study-plans/send", POST, body);
  }

  listStudyPlans(filters) {
    return this.call("/study-plans/plans", GET, activeFilters(filters));
  }

  getStudyPlan(id) {
    return this.call(`/study-plans/plans/${id}`, GET);
  }

  listStudyPlanReviews(filters) {
    return this.call("/study-plans/review", GET, activeFilters(filters));
  }

  gradeStudyPlanItem(itemId, body) {
    return this.call(`/study-plans/items/${itemId}/grade`, PUT, body);
  }

  cancelStudyPlan(id) {
    return this.call(`/study-plans/plans/${id}`, DELETE);
  }

  getCoinsDaily() {
    return this.call("/coins/daily", GET);
  }

  runCoinsDaily() {
    return this.call("/coins/daily/run", POST);
  }
}

const api = new PitchApi(process.env.VUE_APP_PITCH_API);
export default api;
